import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useGetMatieresByFiltersQuery } from '../../services/matieresApi';
import { useGetEtudiantsQuery, useGetChoixFormulaireQuery } from '../../services/etudiantsApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import {
  useGetNotesDevoirsQuery,
  useSaisieMatricielleMutation,
} from '../../services/notesDevoirsApi';
import { CheckCircle2, XCircle, Save, RefreshCw, Lock, Loader } from 'lucide-react';
import AutoSaveIndicator, { AutoSaveStatus } from '../../components/ui/AutoSaveIndicator';
import { SectionCard } from '../../components/ui/SectionCard';
import { AlertBanner } from '../../components/ui/AlertBanner';
import { typeFormationForClasse } from '../../utils/typeFormation';

interface RowNote {
  etudiantId: number;
  matricule: string;
  nom: string;
  prenoms: string;
  noteId: number | null;
  devoir1: number | null;
  absent1: boolean;
  devoir2: number | null;
  absent2: boolean;
  moyenne: number | null;
  verrouille: boolean;
}

interface Props {
  anneeAcademique: string;
}

function calcMoyenne(d1: number | null, abs1: boolean, d2: number | null, abs2: boolean): number | null {
  const n1 = abs1 ? 0 : d1;
  const n2 = abs2 ? 0 : d2;
  if (n1 !== null && n2 !== null) return Math.round(((n1 + n2) / 2) * 100) / 100;
  if (n1 !== null) return n1;
  if (n2 !== null) return n2;
  return null;
}

const SaisieParClasse: React.FC<Props> = ({ anneeAcademique }) => {
  const { data: choixFormulaire, isLoading: loadingChoix } = useGetChoixFormulaireQuery();
  const { data: allClasses = [] } = useGetClassesQuery({ actif: true });

  const [niveau, setNiveau] = useState('');
  const [classe, setClasse] = useState('');
  const [poleFilter, setPoleFilter] = useState('');
  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [rows, setRows] = useState<RowNote[]>([]);
  const [modified, setModified] = useState<Set<number>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Auto-save
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs des inputs pour navigation clavier (key = "etudiantId-d1" | "etudiantId-d2")
  const inputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());
  const setRef = useCallback((key: string) => (el: HTMLInputElement | null) => {
    if (el) inputRefs.current.set(key, el);
    else inputRefs.current.delete(key);
  }, []);

  const [saisieMatricielle, { isLoading: isSaving }] = useSaisieMatricielleMutation();

  // Niveaux depuis choixFormulaire
  const niveaux = useMemo(() => {
    if (!choixFormulaire?.niveaux) return [];
    return choixFormulaire.niveaux.filter(n => n.count > 0).map(n => n.code);
  }, [choixFormulaire]);

  // Classes filtrées par niveau et pôle
  const classes = useMemo(() => {
    if (!niveau) return [];
    let filtered = allClasses.filter(c => c.niveau === niveau);
    if (poleFilter) {
      filtered = filtered.filter(c => c.pole_nom === poleFilter);
    }
    return filtered.map(c => ({ code: c.code, label: `${c.code} — ${c.option_nom}`, option_nom: c.option_nom, count: 0 }));
  }, [allClasses, niveau, poleFilter]);

  // Option de la classe sélectionnée
  const selectedOptionNom = useMemo(() => {
    if (!classe) return undefined;
    const found = classes.find(c => c.code === classe);
    return found?.option_nom;
  }, [classe, classes]);

  // Convertir niveau + S1/S2 en numéro de semestre backend (1-6)
  const semestreNum = useMemo(() => {
    const map: Record<string, Record<string, number>> = {
      'L1': { 'S1': 1, 'S2': 2 },
      'L2': { 'S1': 3, 'S2': 4 },
      'L3': { 'S1': 5, 'S2': 6 },
    };
    return map[niveau]?.[semestre];
  }, [niveau, semestre]);

  // Matières filtrées par niveau, option et semestre
  const { data: matieres, isLoading: loadingMatieres } = useGetMatieresByFiltersQuery(
    {
      niveau: niveau || undefined,
      option: selectedOptionNom,
      semestre: semestreNum,
    },
    { skip: !niveau || !selectedOptionNom }
  );

  // Étudiants de la classe ; CS si la classe finit par "-CS", sinon FI+DD.
  const { data: etudiants, isLoading: loadingEtudiants } = useGetEtudiantsQuery(
    classe ? { classe, type_formation: typeFormationForClasse(classe) } : {},
    { skip: !classe }
  );

  // Notes existantes pour la matière/semestre
  const { data: notesExistantes, isLoading: loadingNotes, refetch: refetchNotes } = useGetNotesDevoirsQuery(
    matiereId
      ? { matiere_id: matiereId, annee_academique: anneeAcademique, semestre }
      : {},
    { skip: !matiereId || !anneeAcademique }
  );

  // Construire les lignes du tableau
  const buildRows = useCallback(() => {
    if (!etudiants || etudiants.length === 0) return;
    const newRows: RowNote[] = etudiants.map((etudiant) => {
      const existing = notesExistantes?.find(
        (n: any) => n.etudiant === etudiant.id || n.etudiant_matricule === etudiant.matricule
      );
      return {
        etudiantId: etudiant.id,
        matricule: etudiant.matricule,
        nom: etudiant.nom,
        prenoms: etudiant.prenoms,
        noteId: existing?.id ?? null,
        devoir1: existing?.note1 != null ? parseFloat(String(existing.note1)) : null,
        absent1: existing?.absent1 ?? false,
        devoir2: existing?.note2 != null ? parseFloat(String(existing.note2)) : null,
        absent2: existing?.absent2 ?? false,
        moyenne: existing?.moyenne_devoirs != null ? parseFloat(String(existing.moyenne_devoirs)) : null,
        verrouille: existing?.verrouille ?? false,
      };
    });
    setRows(newRows);
    setModified(new Set());
  }, [etudiants, notesExistantes]);

  useEffect(() => {
    buildRows();
  }, [buildRows]);

  const handleChange = (etudiantId: number, field: keyof RowNote, raw: any) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.etudiantId !== etudiantId) return row;
        const updated = { ...row };
        if (field === 'devoir1' || field === 'devoir2') {
          const val = raw === '' ? null : parseFloat(raw);
          if (val !== null && (val < 0 || val > 20)) return row;
          (updated as any)[field] = val;
        } else if (field === 'absent1') {
          updated.absent1 = raw;
          if (raw) updated.devoir1 = 0;
        } else if (field === 'absent2') {
          updated.absent2 = raw;
          if (raw) updated.devoir2 = 0;
        }
        updated.moyenne = calcMoyenne(updated.devoir1, updated.absent1, updated.devoir2, updated.absent2);
        return updated;
      })
    );
    setModified((prev) => new Set(prev).add(etudiantId));
    setMessage(null);
  };

  // Sauvegarde unique partagée par auto-save et bouton manuel.
  // silent=true → pas de message UI succès, mais on garde les erreurs visibles.
  const performSave = useCallback(async (silent: boolean): Promise<boolean> => {
    if (!matiereId || !anneeAcademique) return false;
    const toSave = rows
      .filter((r) => modified.has(r.etudiantId))
      .map((r) => ({
        etudiant_id: r.etudiantId,
        note1: r.devoir1,
        absent1: r.absent1,
        note2: r.devoir2,
        absent2: r.absent2,
      }));
    if (toSave.length === 0) return true;

    setAutoSaveStatus('saving');
    try {
      const result = await saisieMatricielle({
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre,
        notes: toSave,
      }).unwrap();
      const saved = (result.crees || 0) + (result.modifies || 0);
      const errs = result.erreurs || 0;
      if (errs > 0 && saved === 0) {
        const firstErr = result.details?.find((d: any) => d.status === 'error')?.error || '';
        setAutoSaveStatus('error');
        if (!silent) {
          setMessage({ type: 'error', text: `Aucune note enregistrée. ${firstErr}` });
        }
        return false;
      }
      setAutoSaveStatus('saved');
      setLastSaveTime(new Date());
      setModified(new Set());
      if (!silent || errs > 0) {
        if (errs > 0) {
          setMessage({
            type: 'error',
            text: `${saved} note(s) enregistrée(s), ${errs} erreur(s). Vérifiez les notes verrouillées.`,
          });
        } else {
          setMessage({ type: 'success', text: `${saved} note(s) enregistrée(s) avec succès !` });
        }
      }
      refetchNotes();
      return errs === 0;
    } catch (err: any) {
      setAutoSaveStatus('error');
      if (!silent) {
        setMessage({
          type: 'error',
          text: err?.data?.error || err?.data?.message || 'Erreur lors de l\'enregistrement.',
        });
      }
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matiereId, anneeAcademique, semestre, rows, modified]);

  // Auto-save désactivé sur demande utilisateur — risquait d'écraser des notes
  // en cours de saisie. Sauvegarde uniquement via le bouton manuel.

  // Avertissement avant fermeture si modifications non sauvegardées
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (modified.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modified]);

  // Navigation clavier + raccourci C/Espace pour copier D1→D2 et passer au suivant
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIdx: number,
    col: 'd1' | 'd2',
  ) => {
    const max = rows.length - 1;

    // Raccourci C ou Espace : copie D1 → D2 + focus D1 du suivant
    if (e.key === 'c' || e.key === 'C' || e.key === ' ') {
      const row = rows[rowIdx];
      if (!row || row.verrouille || row.devoir1 === null) return;
      e.preventDefault();
      setRows(prev => prev.map(r => {
        if (r.etudiantId !== row.etudiantId) return r;
        const updated = { ...r, devoir2: r.devoir1, absent2: false };
        updated.moyenne = calcMoyenne(updated.devoir1, updated.absent1, updated.devoir2, updated.absent2);
        return updated;
      }));
      setModified(prev => new Set(prev).add(row.etudiantId));
      if (rowIdx < max) {
        const nextRow = rows[rowIdx + 1];
        setTimeout(() => {
          const el = inputRefs.current.get(`${nextRow.etudiantId}-d1`);
          if (el) { el.focus(); el.select(); }
        }, 0);
      }
      return;
    }

    let newRow = rowIdx;
    let newCol: 'd1' | 'd2' = col;

    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      if (rowIdx < max) newRow = rowIdx + 1;
      else return;
    } else if (e.key === 'ArrowUp') {
      if (rowIdx > 0) newRow = rowIdx - 1;
      else return;
    } else if (e.key === 'ArrowLeft') {
      const target = e.currentTarget;
      if (target.selectionStart !== 0) return;
      if (col === 'd2') newCol = 'd1';
      else if (rowIdx > 0) { newRow = rowIdx - 1; newCol = 'd2'; }
      else return;
    } else if (e.key === 'ArrowRight') {
      const target = e.currentTarget;
      if (target.selectionStart !== (target.value?.length ?? 0)) return;
      if (col === 'd1') newCol = 'd2';
      else if (rowIdx < max) { newRow = rowIdx + 1; newCol = 'd1'; }
      else return;
    } else {
      return;
    }

    e.preventDefault();
    const targetRow = rows[newRow];
    if (!targetRow) return;
    const el = inputRefs.current.get(`${targetRow.etudiantId}-${newCol}`);
    if (el) { el.focus(); el.select(); }
  };

  const handleSave = async () => {
    setMessage(null);
    if (modified.size === 0) {
      setMessage({ type: 'error', text: 'Aucune modification à enregistrer.' });
      return;
    }
    await performSave(false);
  };

  const canLoad = !!(classe && matiereId && anneeAcademique);
  const isLoading = loadingEtudiants || (loadingNotes && !!matiereId);

  const moyenneClasse = useMemo(() => {
    const valids = rows.filter((r) => r.moyenne !== null);
    if (!valids.length) return null;
    return Math.round((valids.reduce((acc, r) => acc + r.moyenne!, 0) / valids.length) * 100) / 100;
  }, [rows]);

  const selectedMatiere = Array.isArray(matieres) ? matieres.find((m) => m.id === matiereId) : undefined;

  return (
    <div className="space-y-5">
      {/* Filtres (unifié — refonte vague 3 lot 2) */}
      <SectionCard title="Sélection de la classe et matière" padding="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Pôle */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Pôle
            </label>
            <select
              value={poleFilter}
              onChange={(e) => { setPoleFilter(e.target.value); setClasse(''); setRows([]); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les pôles</option>
              {['Polytechnique', 'Commerce', 'Juridique'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Niveau */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Niveau
            </label>
            <select
              value={niveau}
              onChange={(e) => { setNiveau(e.target.value); setClasse(''); setMatiereId(null); setRows([]); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Niveau --</option>
              {niveaux.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Classe */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Classe
            </label>
            <select
              value={classe}
              onChange={(e) => { setClasse(e.target.value); setMatiereId(null); setRows([]); }}
              disabled={!niveau}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600"
            >
              <option value="">-- Classe --</option>
              {classes.map((c) => (
                <option key={c.code} value={c.code}>{c.label} ({c.count})</option>
              ))}
            </select>
          </div>

          {/* Matière */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Matière
            </label>
            {loadingMatieres || loadingChoix ? (
              <div className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-sm text-gray-400">
                Chargement…
              </div>
            ) : (
              <select
                value={matiereId || ''}
                onChange={(e) => setMatiereId(e.target.value ? parseInt(e.target.value) : null)}
                disabled={!classe}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600"
              >
                <option value="">-- Matière --</option>
                {(matieres || []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code_matiere} – {m.nom_matiere}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Semestre */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Semestre
            </label>
            <select
              value={semestre}
              onChange={(e) => setSemestre(e.target.value as 'S1' | 'S2')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="S1">Semestre 1</option>
              <option value="S2">Semestre 2</option>
            </select>
          </div>
        </div>

        {!canLoad && (
          <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
            Sélectionnez un niveau, une classe et une matière pour charger la liste des étudiants.
          </p>
        )}
      </SectionCard>

      {/* Message feedback (unifié) */}
      {message && (
        <AlertBanner
          type={message.type === 'success' ? 'success' : 'error'}
          message={message.text}
        />
      )}

      {/* Tableau de saisie */}
      {canLoad && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
          {/* Header du tableau */}
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {classe} — {selectedMatiere?.nom_matiere}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {rows.length} étudiant(s) — {modified.size} modification(s) non enregistrée(s)
                {moyenneClasse !== null && ` — Moyenne classe : ${moyenneClasse.toFixed(2)}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <AutoSaveIndicator status={autoSaveStatus} lastSaveTime={lastSaveTime} pendingCount={modified.size} />
              <button
                onClick={buildRows}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                title="Réinitialiser"
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={handleSave}
                disabled={modified.size === 0 || isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {isSaving ? <Loader size={15} className="animate-spin" /> : <Save size={15} />}
                {isSaving
                  ? 'Enregistrement…'
                  : `Enregistrer (${modified.size})`}
              </button>
            </div>
          </div>

          {/* Aide raccourcis clavier */}
          {rows.length > 0 && (
            <div className="px-5 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-3">
              <span className="font-semibold text-gray-600 dark:text-gray-300">Raccourcis :</span>
              <span>
                <kbd className="px-1 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 rounded border border-amber-300 font-bold">C</kbd>
                {' '}ou{' '}
                <kbd className="px-1 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 rounded border border-amber-300 font-bold">Espace</kbd>
                {' '}copie D1→D2 + étudiant suivant
              </span>
              <span><kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">↑</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">↓</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">←</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">→</kbd> navigation</span>
              <span><kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">Entrée</kbd> ligne suivante</span>
              <span className="text-blue-700 dark:text-blue-400">↳ Auto-save 2s après dernière saisie</span>
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader size={32} className="animate-spin text-blue-500 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Chargement des étudiants…</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-gray-400 dark:text-gray-500">
              <p className="text-sm">Aucun étudiant trouvé pour la classe {classe}.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3 text-left">#</th>
                    <th className="px-4 py-3 text-left">Matricule</th>
                    <th className="px-4 py-3 text-left">Nom</th>
                    <th className="px-4 py-3 text-left">Prénoms</th>
                    <th className="px-4 py-3 text-center">Devoir 1</th>
                    <th className="px-4 py-3 text-center">Devoir 2</th>
                    <th className="px-4 py-3 text-center">Moyenne</th>
                    <th className="px-4 py-3 text-center">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row, idx) => {
                    const isDirty = modified.has(row.etudiantId);
                    return (
                      <tr
                        key={row.etudiantId}
                        className={`transition-colors ${
                          isDirty
                            ? 'bg-amber-50 dark:bg-amber-900/10'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                        }`}
                      >
                        <td className="px-4 py-2.5 text-gray-400 dark:text-gray-500 tabular-nums">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-700 dark:text-gray-300">
                          {row.matricule}
                        </td>
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                          {row.nom}
                        </td>
                        <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                          {row.prenoms}
                        </td>
                        {/* Devoir 1 (fix 2026-08-27 : plus de case Absent, vide au blur = 0) */}
                        <td className="px-3 py-2">
                          <input
                            ref={setRef(`${row.etudiantId}-d1`)}
                            type="number"
                            step="0.25"
                            min="0"
                            max="20"
                            value={row.devoir1 !== null ? row.devoir1 : ''}
                            onChange={(e) => handleChange(row.etudiantId, 'devoir1', e.target.value)}
                            onBlur={(e) => {
                              if (e.target.value.trim() === '' && !row.verrouille) {
                                handleChange(row.etudiantId, 'devoir1', '0');
                              }
                            }}
                            onKeyDown={(e) => handleKeyDown(e, idx, 'd1')}
                            disabled={row.verrouille}
                            className="w-20 px-2 py-1.5 text-center border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:text-gray-400"
                          />
                        </td>
                        {/* Devoir 2 */}
                        <td className="px-3 py-2">
                          <input
                            ref={setRef(`${row.etudiantId}-d2`)}
                            type="number"
                            step="0.25"
                            min="0"
                            max="20"
                            value={row.devoir2 !== null ? row.devoir2 : ''}
                            onChange={(e) => handleChange(row.etudiantId, 'devoir2', e.target.value)}
                            onBlur={(e) => {
                              if (e.target.value.trim() === '' && !row.verrouille) {
                                handleChange(row.etudiantId, 'devoir2', '0');
                              }
                            }}
                            onKeyDown={(e) => handleKeyDown(e, idx, 'd2')}
                            disabled={row.verrouille}
                            className="w-20 px-2 py-1.5 text-center border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:text-gray-400"
                          />
                        </td>
                        {/* Moyenne */}
                        <td className="px-3 py-2 text-center">
                          <span
                            className={`font-semibold tabular-nums ${
                              row.moyenne === null
                                ? 'text-gray-300 dark:text-gray-600'
                                : row.moyenne >= 10
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}
                          >
                            {row.moyenne !== null ? row.moyenne.toFixed(2) : '—'}
                          </span>
                        </td>
                        {/* Statut */}
                        <td className="px-3 py-2 text-center">
                          {row.verrouille ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-xs font-medium">
                              <Lock size={10} /> Verrouillé
                            </span>
                          ) : isDirty ? (
                            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium">
                              Modifié
                            </span>
                          ) : row.noteId ? (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
                              Enregistré
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full text-xs">
                              Vide
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Footer stats */}
                {rows.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-200 dark:border-gray-600">
                      <td colSpan={4} className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300">
                        {rows.length} étudiant(s) — {rows.filter((r) => r.noteId !== null).length} note(s) saisie(s)
                      </td>
                      <td colSpan={4} className="px-4 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
                        {rows.filter((r) => r.absent1).length} absent(s) D1 ·{' '}
                        {rows.filter((r) => r.absent2).length} absent(s) D2
                      </td>
                      <td className="px-3 py-3 text-center text-xs font-bold text-gray-700 dark:text-gray-200">
                        {moyenneClasse !== null ? moyenneClasse.toFixed(2) : '—'}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SaisieParClasse;
